import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Select } from '@ngxs/store';
import { debounce } from 'lodash-es';
import { Observable, Subscription } from 'rxjs';
import { DictionaryItem, DictionaryItemNumber } from '../../../../domain/dictionary.model';
import { ValidationError } from '../../../../domain/error.model';
import { VehicleDefinition, VehicleDefinitionMode, VehicleType } from '../../../../domain/product.domain';
import { LsnErrorStateMatcher } from '../../../shared/middleware/error-state-matcher';
import { SuffixLabelPipe } from '../../../shared/pipe/suffix-label.pipe';
import { EurotaxService } from '../../service/eurotax.service';
import { ErrorsState } from '../../store/errors.state';

@Component({
    selector: 'app-eurotax-form',
    templateUrl: './eurotax-form.component.html',
    styleUrls: ['./eurotax-form.component.scss']
})
export class EurotaxFormComponent implements OnInit, OnDestroy {

    @Input() title: string = null;
    @Input() prefix: string = null;
    @Input() data: VehicleDefinition = null;
    @Input() aside: boolean = true

    subscriptions: Subscription[] = [];
    errors: ValidationError[] = [];

    matchers: any;

    @Select(ErrorsState.getMainErrors) mainErrors$: Observable<ValidationError[]>;
    @Select(ErrorsState.getAsideErrors) asideErrors$: Observable<ValidationError[]>;

    categories: VehicleType[] = [];
    makes: VehicleType[] = [];
    models: VehicleType[] = [];
    bodyTypes: DictionaryItem[] = [];
    doors: VehicleType[] = [];
    fuelTypes: DictionaryItem[] = [];
    capacities: DictionaryItemNumber[] = [];
    versions: VehicleType[] = [];
    gearBoxTypes: DictionaryItem[] = [];

    get outOfEurotax() {
        // shouldn't happen, but still, lets set default
        if (this.data.mode === null) {
            this.data.mode = VehicleDefinitionMode.EUROTAX;
        }
        return this.data.mode === VehicleDefinitionMode.MANUAL;
    }

    set outOfEurotax(value) {
        this.data.mode = value
            ? VehicleDefinitionMode.MANUAL
            : VehicleDefinitionMode.EUROTAX;
    }

    constructor(private eurotaxService: EurotaxService, private suffixLabel: SuffixLabelPipe) {
    }

    ngOnInit(): void {
        if (this.data.eurotax === null) {
            this.data.eurotax = new VehicleType();
        }

        this.subscriptions.push(
            this.aside
                ? this.asideErrors$.subscribe(errors => this.errors = errors)
                : this.mainErrors$.subscribe(errors => this.errors = errors)
        );

        this.prepareMatchers();
        this.downloadCategories();
    }


    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private prepareMatchers() {
        const merge = value => this.prefix !== null
            ? `${this.prefix}.${value}`
            : value;

        this.matchers = {
            productionYear: new LsnErrorStateMatcher(this, merge('productionYear')),
            eurotax: {
                categoryId: new LsnErrorStateMatcher(this, merge('eurotax.categoryId')),
                makeId: new LsnErrorStateMatcher(this, merge('eurotax.makeId')),
                modelId: new LsnErrorStateMatcher(this, merge('eurotax.modelId')),
                bodyType: new LsnErrorStateMatcher(this, merge('eurotax.bodyType')),
                doorCount: new LsnErrorStateMatcher(this, merge('eurotax.doorCount')),
                fuelType: new LsnErrorStateMatcher(this, merge('eurotax.fuelType')),
                capacity: new LsnErrorStateMatcher(this, merge('eurotax.capacity')),
                gearBoxType: new LsnErrorStateMatcher(this, merge('eurotax.gearBoxType')),
                typeId: new LsnErrorStateMatcher(this, merge('eurotax.typeId')),
            }
        };
    }

    set categoryId(value) {
        this.data.eurotax.categoryId = value;
        if (value !== null) {
            this.downloadMakesDebounced();
        } else {
            this.clearCategories(true);
        }
    }

    set makeId(value) {
        this.data.eurotax.makeId = value;
        this.downloadModels();
    }

    set modelId(value) {
        this.data.eurotax.modelId = value;
        this.downloadBodyType();
    }

    set bodyType(value) {
        this.data.eurotax.bodyType = value;
        this.downloadDoors()
    }

    set doorCount(value) {
        this.data.eurotax.doorCount = value;
        this.downloadFuelType();
    }

    set fuelType(value) {
        this.data.eurotax.fuelType = value;
        this.downloadCapacities();
    }

    set capacity(value) {
        this.data.eurotax.capacity = value;
        this.downloadGearBoxTypes();
    }

    set gearBoxType(value) {
        this.data.eurotax.gearBoxType = value;
        this.downloadVersions();
    }

    downloadCategories() {
        this.eurotaxService.getCategory()
            .subscribe(categories => {
                this.categories = categories;
                if (categories.map(t => t.categoryId).indexOf(this.data.eurotax.categoryId) === -1) {
                    this.clearCategories();
                }
                if (categories.length === 1) {
                    this.data.eurotax.categoryId = categories[0].categoryId;
                }
                this.downloadMakes();
            });
    }

    downloadMakesDebounced = debounce(this.downloadMakes, 50);

    downloadMakes() {
        if (this.invalidRequestMake) {
            return this.clearMake(true);
        }

        this.eurotaxService.getMakes(this.data.eurotax.categoryId, this.data.productionYear)
            .subscribe(makes => {
                this.makes = makes;
                if (makes.map(t => t.makeId).indexOf(this.data.eurotax.makeId) === -1) {
                    this.clearMake();
                }
                if (makes.length === 1) {
                    this.data.eurotax.makeId = makes[0].makeId;
                }
                this.downloadModels();
            });
    }

    downloadModels() {
        if (this.invalidRequestModel) {
            return this.clearModel(true);
        }

        this.eurotaxService.getModels(this.data.eurotax.categoryId, this.data.productionYear, this.data.eurotax.makeId)
            .subscribe(models => {
                this.models = models;
                if (models.map(t => t.modelId).indexOf(this.data.eurotax.modelId) === -1) {
                    this.clearModel();
                }
                if (models.length === 1) {
                    this.data.eurotax.modelId = models[0].modelId;
                }
                this.downloadBodyType();
            });
    }

    downloadBodyType() {
        if (this.invalidRequestBodyType) {
            return this.clearBodyType(true);
        }

        const e = this.data.eurotax;
        this.eurotaxService.getBodyTypes(e.categoryId, this.data.productionYear, e.makeId, e.modelId)
            .subscribe(bodyTypes => {
                this.bodyTypes = bodyTypes.map(e => new DictionaryItem(e.bodyType, e.bodyTypeItem ? e.bodyTypeItem.description : ''));
                if (bodyTypes.map(t => t.bodyType).indexOf(this.data.eurotax.bodyType) === -1) {
                    this.clearBodyType();
                }
                if (bodyTypes.length === 1) {
                    this.data.eurotax.bodyType = bodyTypes[0].bodyType;
                }
                this.downloadDoors();
            });
    }

    downloadDoors() {
        if (this.invalidRequestDoorCount) {
            return this.clearDoorCount(true);
        }

        const e = this.data.eurotax;
        this.eurotaxService.getDoors(e.categoryId, this.data.productionYear, e.makeId, e.modelId, e.bodyType)
            .subscribe(doors => {
                this.doors = doors;
                if (doors.map(t => t.doorCount).indexOf(this.data.eurotax.doorCount) === -1) {
                    this.clearDoorCount();
                }
                if (doors.length === 1) {
                    this.data.eurotax.doorCount = doors[0].doorCount;
                }
                this.downloadFuelType();
            });
    }

    downloadFuelType() {
        if (this.invalidRequestFuelType) {
            return this.clearFuelType(true);
        }

        const e = this.data.eurotax;
        this.eurotaxService.getFuelTypes(e.categoryId, this.data.productionYear, e.makeId, e.modelId, e.bodyType, e.doorCount)
            .subscribe(fuelTypes => {
                this.fuelTypes = fuelTypes.map(element => new DictionaryItem(element.fuelType, element.fuelTypeItem.description));
                if (fuelTypes.map(t => t.fuelType).indexOf(this.data.eurotax.fuelType) === -1) {
                    this.clearFuelType();
                }
                if (fuelTypes.length === 1) {
                    this.data.eurotax.fuelType = fuelTypes[0].fuelType;
                }
                this.downloadCapacities();
            });
    }

    downloadCapacities() {
        if (this.invalidRequestCapacities) {
            return this.clearCapacity(true);
        }

        const e = this.data.eurotax;
        this.eurotaxService.getCapacities(e.categoryId, this.data.productionYear, e.makeId, e.modelId, e.bodyType, e.doorCount, e.fuelType)
            .subscribe(capacities => {
                this.capacities = capacities.map(e => new DictionaryItemNumber(e.capacity, this.suffixLabel.transform(e.capacity, 'LABEL.SUFFIX_CM3')));
                if (capacities.map(t => t.capacity).indexOf(this.data.eurotax.capacity) === -1) {
                    this.clearCapacity();
                }
                if (capacities.length === 1) {
                    this.data.eurotax.capacity = capacities[0].capacity;
                }
                this.downloadGearBoxTypes();
            });
    }

    downloadGearBoxTypes() {
        if (this.invalidRequestGearBoxTypes) {
            return this.clarGearBoxType(true);
        }

        const e = this.data.eurotax;
        this.eurotaxService.getGearBoxTypes(e.categoryId, this.data.productionYear, e.makeId, e.modelId, e.bodyType, e.doorCount, e.fuelType, e.capacity)
            .subscribe(gearBoxTypes => {
                this.gearBoxTypes = gearBoxTypes.map(e => new DictionaryItem(e.gearBoxType, e.gearBoxTypeItem.description));
                if (gearBoxTypes.map(t => t.gearBoxType).indexOf(this.data.eurotax.gearBoxType) === -1) {
                    this.clarGearBoxType();
                }
                if (gearBoxTypes.length === 1) {
                    this.data.eurotax.gearBoxType = gearBoxTypes[0].gearBoxType;
                }
                this.downloadVersions();
            });
    }

    downloadVersions() {
        if (this.invalidRequestVersions) {
            return this.clearTypeId(true);
        }

        const e = this.data.eurotax;
        this.eurotaxService.getVersions(e.categoryId, this.data.productionYear, e.makeId, e.modelId, e.bodyType, e.doorCount, e.fuelType, e.capacity, e.gearBoxType)
            .subscribe(versions => {
                this.versions = versions;
                if (versions.map(t => t.typeId).indexOf(this.data.eurotax.typeId) === -1) {
                    this.clearTypeId();
                }
                if (versions.length === 1) {
                    this.data.eurotax.typeId = versions[0].typeId;
                }
            })

    }

    get invalidRequestMake() {
        return this.data.eurotax.categoryId === null ||
            this.data.productionYear == null ||
            Math.ceil(Math.log(this.data.productionYear + 1) / Math.LN10) !== 4;
    }

    get invalidRequestModel() {
        return this.invalidRequestMake ||
            this.data.eurotax.makeId === null
    }

    get invalidRequestBodyType() {
        return this.invalidRequestModel ||
            this.data.eurotax.modelId === null
    }

    get invalidRequestDoorCount() {
        return this.invalidRequestBodyType ||
            this.data.eurotax.bodyType === null
    }

    get invalidRequestFuelType() {
        return this.invalidRequestDoorCount ||
            this.data.eurotax.doorCount === null
    }

    get invalidRequestCapacities() {
        return this.invalidRequestFuelType ||
            this.data.eurotax.fuelType === null;
    }

    get invalidRequestGearBoxTypes() {
        return this.invalidRequestCapacities ||
            this.data.eurotax.capacity === null;
    }

    get invalidRequestVersions() {
        return this.invalidRequestGearBoxTypes ||
            this.data.eurotax.gearBoxType === null;
    }


    private clearCategories(full = false) {
        this.data.eurotax.categoryId = null;
        this.clearMake(full);
    }

    private clearMake(full = false) {
        this.data.eurotax.makeId = null;
        if (full) {
            this.makes = [];
        }
        this.clearModel(full);
    }

    private clearModel(full = false) {
        this.data.eurotax.modelId = null;
        if (full) {
            this.models = [];
        }
        this.clearBodyType(full);
    }

    private clearBodyType(full = false) {
        this.data.eurotax.bodyType = null;
        if (full) {
            this.bodyTypes = [];
        }
        this.clearDoorCount(full);
    }

    private clearDoorCount(full = false) {
        this.data.eurotax.doorCount = null;
        if (full) {
            this.doors = [];
        }
        this.clearFuelType(full);
    }

    private clearFuelType(full = false) {
        this.data.eurotax.fuelType = null;
        if (full) {
            this.fuelTypes = [];
        }
        this.clearCapacity(full);
    }

    private clearCapacity(full = false) {
        this.data.eurotax.capacity = null;
        if (full) {
            this.capacities = [];
        }
        this.clarGearBoxType(full);
    }

    private clarGearBoxType(full = false) {
        this.data.eurotax.gearBoxType = null;
        if (full) {
            this.gearBoxTypes = [];
        }
        this.clearTypeId(full);
    }

    private clearTypeId(full = false) {
        this.data.eurotax.typeId = null;
        if (full) {
            this.versions = [];
        }
    }
}
